export function normalizeTaxIds(raw: unknown): number[] {
    if (!Array.isArray(raw)) return [];
    return raw.map(x => Number(x)).filter(x => Number.isFinite(x) && x > 0);
}

/**
 * Per-tax metadata fetched from the server.
 * percentage: decimal multiplier (e.g. 0.18 for 18%).
 * calculationBase: NetAmount | GrossAmount | MRP | Quantity
 * taxComputation: percentage | percentage_tax_included | fixed | group_of_taxes
 */
export type TaxLineInfo = {
    percentage: number;
    calculationBase: string;
    taxComputation: string;
    affectBaseOfSubsequentTaxes: boolean;
};

/**
 * Per-tax amounts for a line, aligned index-for-index with `taxes`.
 * Used by print/PDF per-rate breakdowns; computeTaxFromMeta sums these.
 *
 * Base selection per tax:
 *   NetAmount  → subtotal (after discount); grows when affectBaseOfSubsequentTaxes is set.
 *   GrossAmount → gross (qty × price, before discount); never grows.
 *   MRP        → qty × mrp (regulated retail price); falls back to subtotal when mrp is 0.
 *   Quantity   → only with fixed: tax = qty × percentage (rate is an absolute amount per unit).
 *
 * Formula selection:
 *   percentage_tax_included → reverse extraction: base × rate / (1 + rate)
 *   fixed + Quantity        → qty × rate
 *   otherwise               → base × rate
 */
export function computeTaxPartsFromMeta(
    taxes: TaxLineInfo[],
    subtotal: number,
    gross: number,
    qty: number,
    mrp: number
): number[] {
    let cumulativeBase = subtotal;
    const parts: number[] = [];
    for (const tax of taxes) {
        let taxAmt: number;
        const cb = tax.calculationBase || 'NetAmount';
        if (tax.taxComputation === 'fixed' && cb === 'Quantity') {
            taxAmt = qty > 0 && tax.percentage > 0 ? roundMoney(qty * tax.percentage) : 0;
        } else {
            let base: number;
            if (cb === 'MRP') {
                base = mrp > 0 ? roundMoney(qty * mrp) : cumulativeBase;
            } else if (cb === 'GrossAmount') {
                base = gross;
            } else {
                base = cumulativeBase;
            }
            if (base <= 0 || tax.percentage <= 0) {
                taxAmt = 0;
            } else if (tax.taxComputation === 'percentage_tax_included') {
                taxAmt = roundMoney(base * tax.percentage / (1 + tax.percentage));
            } else {
                taxAmt = roundMoney(base * tax.percentage);
            }
        }
        parts.push(taxAmt);
        // Grow the cumulative base for subsequent taxes when this tax opts in.
        // MRP taxes are included: PST on "net + prior taxes" must accumulate the MRP tax amount.
        if (tax.affectBaseOfSubsequentTaxes && cb !== 'Quantity') {
            cumulativeBase = roundMoney(cumulativeBase + taxAmt);
        }
    }
    return parts;
}

/** Total tax for a line — sum of computeTaxPartsFromMeta (see it for the per-tax rules). */
export function computeTaxFromMeta(
    taxes: TaxLineInfo[],
    subtotal: number,
    gross: number,
    qty: number,
    mrp: number
): number {
    let total = 0;
    for (const part of computeTaxPartsFromMeta(taxes, subtotal, gross, qty, mrp)) total += part;
    return roundMoney(total);
}

/**
 * Normalize an fbrSalesTaxRates API row into TaxLineInfo. Single place for the
 * "percentage + includedInPrice=yes → percentage_tax_included" rule so grids and
 * print breakdowns can't drift apart.
 */
export function taxLineInfoFromRateRow(row: Record<string, unknown>): TaxLineInfo {
    const rawTaxComp = String(row.taxComputation ?? row.TaxComputation ?? 'percentage');
    const rawIncluded = String(row.includedInPrice ?? row.IncludedInPrice ?? '')
        .toLowerCase()
        .trim();
    return {
        percentage: Number(row.percentage) || 0,
        calculationBase: String(row.calculationBase ?? row.CalculationBase ?? 'NetAmount'),
        taxComputation:
            rawTaxComp === 'percentage' && rawIncluded === 'yes'
                ? 'percentage_tax_included'
                : rawTaxComp,
        affectBaseOfSubsequentTaxes: Boolean(
            row.affectBaseOfSubsequentTaxes ?? row.AffectBaseOfSubsequentTaxes ?? false
        ),
    };
}

export function parseMoney(raw: unknown): number {
    if (raw == null || raw === '') return 0;
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
}

export function roundMoney(n: number): number {
    return Math.round(n * 1000) / 1000;
}

export function partLineGross(qty: number, price: number): number {
    return roundMoney(qty * price);
}

export function partLineSubtotal(qty: number, price: number, depreciationAmount: number): number {
    return Math.max(0, roundMoney(partLineGross(qty, price) - depreciationAmount));
}

/** Tax base: MRP × qty when fixed notified applies; otherwise line subtotal. */
export function partLineTaxBase(
    subtotal: number,
    qty: number,
    fixedNotifiedApplicable: boolean,
    mrpRateValue: number
): number {
    const mrp = Number(mrpRateValue) || 0;
    if (fixedNotifiedApplicable && mrp > 0) return roundMoney(qty * mrp);
    return subtotal;
}

export function partLineTaxAmount(taxBase: number, taxPercentage: number): number | null {
    const pct = Number(taxPercentage) || 0;
    if (taxBase <= 0 || pct <= 0) return null;
    return roundMoney(taxBase * pct);
}

export type PartLineAmountInput = {
    quantity?: unknown;
    price?: unknown;
    discount?: unknown;
    discAmt?: unknown;
    depreciationAmount?: unknown;
    taxRate?: unknown;
    fixedNotifiedApplicable?: unknown;
    mrpRateValue?: unknown;
    /** When provided, overrides the single-rate taxRate approach with per-tax calculation. */
    taxes?: TaxLineInfo[];
    /**
     * Alias for `taxes` under the name the line grids store it as on the form row.
     * Lets callers hand a whole form row straight in without remapping the key —
     * forgetting to remap silently downgrades the line to the legacy taxRate path.
     */
    taxLineMeta?: unknown;
};

export function computePartLineAmounts(line: PartLineAmountInput) {
    const qty = parseMoney(line.quantity);
    const price = parseMoney(line.price);
    const gross = partLineGross(qty, price);
    // Disc%: stored as a plain number (e.g. 5 = 5%). discAmt wins if both set.
    const discPct = parseMoney(line.discount);
    const discAmtDirect = parseMoney(line.discAmt);
    const discAmt = discAmtDirect > 0 ? discAmtDirect : (discPct > 0 ? roundMoney(gross * discPct / 100) : 0);
    const dep = parseMoney(line.depreciationAmount);
    const taxRate = parseMoney(line.taxRate);
    const fixedNotified =
        line.fixedNotifiedApplicable === true || line.fixedNotifiedApplicable === 'true';
    const mrp = parseMoney(line.mrpRateValue);
    const subtotal = Math.max(0, roundMoney(gross - discAmt - dep));

    const taxes = Array.isArray(line.taxes) && line.taxes.length > 0
        ? line.taxes
        : Array.isArray(line.taxLineMeta)
          ? (line.taxLineMeta as TaxLineInfo[])
          : [];

    let taxAmount: number;
    let taxBase: number;
    if (taxes.length > 0) {
        taxAmount = computeTaxFromMeta(taxes, subtotal, gross, qty, mrp);
        taxBase = subtotal;
    } else {
        taxBase = partLineTaxBase(subtotal, qty, fixedNotified, mrp);
        taxAmount = partLineTaxAmount(taxBase, taxRate) ?? 0;
    }

    const net = roundMoney(subtotal + taxAmount);
    return { gross, subtotal, taxBase, taxAmount, net, discAmt };
}

export type JobEstimateTotals = {
    jobsTotal: number;
    partsSubtotal: number;
    taxTotal: number;
    totalEstimateAmount: number;
    grandTotal: number;
};

export function computeJobEstimateTotals(
    jobLines: Array<{ labourRate?: unknown; taxAmount?: unknown }> | undefined,
    partsLines: Array<PartLineAmountInput> | undefined
): JobEstimateTotals {
    let jobsTotal = 0;
    let taxTotal = 0;
    for (const row of jobLines ?? []) {
        jobsTotal += parseMoney(row?.labourRate);
        taxTotal += parseMoney(row?.taxAmount);
    }

    let partsSubtotal = 0;
    for (const row of partsLines ?? []) {
        const amounts = computePartLineAmounts(row);
        partsSubtotal += amounts.subtotal;
        taxTotal += amounts.taxAmount;
    }

    const totalEstimateAmount = roundMoney(jobsTotal + partsSubtotal);
    const grandTotal = roundMoney(totalEstimateAmount + taxTotal);
    return {
        jobsTotal: roundMoney(jobsTotal),
        partsSubtotal: roundMoney(partsSubtotal),
        taxTotal: roundMoney(taxTotal),
        totalEstimateAmount,
        grandTotal,
    };
}
